import { Document, Page, View } from "@edclub/print/client";
import {
  renderExplanation,
  renderPageHeader,
  renderSectionHeader,
  renderTable,
} from "@edclub/print/templates";

import { type SummaryReport } from "@/store";

const tableBaseConfig = {
  columnWidths: [35, 65],
  classNames: {
    tableClassName: "mb-2",
    rowClassName: "p-0",
    headerCellClassName: "px-0",
    cellClassName: "px-0",
  },
};

export const PrintReportTemplate = (report: SummaryReport) => {
  return (
    <Document>
      <Page size="A4" className="p-8">
        {renderPageHeader(t("Crossword Report"))}

        <View className="mb-6">
          {renderSectionHeader(t("Performance"))}
          {renderTable({
            ...tableBaseConfig,
            rows: [[t("Score"), `${report.performance.score}/100`]],
          })}
          {renderExplanation(
            t(
              "Score is calculated based on the number of correctly filled cells, the number of words correctly answered, and the number of hints used.",
            ),
          )}
        </View>

        <View className="mb-6">
          {renderSectionHeader(t("Words Summary"))}
          {renderTable({
            ...tableBaseConfig,
            rows: [
              [t("Total words"), report.words.total],
              [t("Fully correct words"), report.words.fullyCorrect],
              [t("Unanswered words"), report.words.notEntered],
              [t("Words with mistakes"), report.words.withMistakes],
            ],
          })}
        </View>

        <View className="mb-6">
          {renderSectionHeader(t("Cell Statistics"))}
          {renderTable({
            ...tableBaseConfig,
            rows: [
              [t("Total playable cells"), report.cells.totalPlayable],
              [t("Default revealed cells"), report.cells.defaultRevealed],
              [t("Cells to fill"), report.cells.toFill],
              [t("Cells filled by user"), report.cells.filledByUser],
              [t("Correct cells by user"), report.cells.correctByUser],
              [t("Incorrect cells by user"), report.cells.incorrectByUser],
              [
                t("Cells filled including hints"),
                report.cells.filledIncludingHints,
              ],
              [
                t("Correct cells including hints"),
                report.cells.correctIncludingHints,
              ],
              [
                t("Incorrect cells including hints"),
                report.cells.incorrectIncludingHints,
              ],
              [t("Completion (user only)"), report.cells.completionPercentUser],
              [
                t("Completion (overall)"),
                report.cells.completionPercentOverall,
              ],
            ],
          })}
        </View>

        <View className="mb-6">
          {renderSectionHeader(t("Hint Usage"))}
          {renderTable({
            ...tableBaseConfig,
            rows: [
              [t("Initial hints available"), report.hints.initialHints],
              [t("Hints remaining"), report.hints.hintsRemaining],
              [t("Cells revealed by hints"), report.hints.hintCellsRevealed],
            ],
          })}
        </View>

        <View className="mb-6" break>
          {renderSectionHeader(t("Details"))}
          <View className="flex flex-col gap-1">
            {report.entries.map((entry) => (
              <View
                key={entry.question}
                className="mb-8 flex flex-col gap-1"
                wrap={false}
              >
                {renderTable({
                  ...tableBaseConfig,
                  rows: [
                    [t("Question"), entry.question],
                    [
                      t("User Answer"),
                      {
                        content: entry.userAnswer,
                        className:
                          entry.userAnswer === entry.correctAnser
                            ? "text-green-600"
                            : "text-red-600",
                      },
                    ],
                    [t("Correct Answer"), entry.correctAnser],
                  ],
                  classNames: {
                    tableClassName: "mb-1",
                    rowClassName: "p-0",
                  },
                })}
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};
